// src/app/services/update.service.ts
import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { ProcessingService } from 'src/app/core/services/processing.service';
import { environment } from 'src/environments/environment';

/** Session guard key — matches the existing `rw:` prefix convention (see rw:auth:logout). */
export const STALE_RECOVERY_STORAGE_KEY = 'rw:stale-recovery';

/**
 * Longest we wait for the service worker to confirm activation before replacing
 * the page anyway. See activateUpdate().
 */
export const ACTIVATION_TIMEOUT_MS = 3000;

@Injectable({ providedIn: 'root' })
export class UpdateService implements OnDestroy {
  private readonly updateAvailableSubject = new BehaviorSubject<boolean>(false);
  readonly updateAvailable$ = this.updateAvailableSubject.asObservable();
  private updateCheckSubscription?: Subscription;
  private versionUpdatesSubscription?: Subscription;
  private unrecoverableSubscription?: Subscription;
  private recoverySubscription?: Subscription;
  private recoveryPending = false;
  private lastSeenNgswTimestamp?: number;
  /** Version we have already announced. Guards against re-arming the banner
   *  for a deploy the user is already being prompted about — one deploy must
   *  never be able to trigger two reloads. */
  private announcedVersion?: number | string;

  constructor(
    private swUpdate: SwUpdate,
    private logger: LoggerService,
    private processingService: ProcessingService,
    private ngZone: NgZone
  ) {
    // Installed BEFORE the guard below, so it is still available to report *why*
    // the flow is inert when the service worker did not register.
    this.installDevHook();

    if (!swUpdate.isEnabled) {
      this.logger.debug('Service worker is not enabled in this environment');
      return;
    }

    this.checkForUpdates();
    void this.pollNgswManifest();

    this.ngZone.runOutsideAngular(() => {
      this.updateCheckSubscription = interval(this.pollIntervalMs).subscribe(() => {
        this.ngZone.run(() => {
          this.checkForUpdates();
          void this.pollNgswManifest();
        });
      });
    });

    this.versionUpdatesSubscription = this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe((event) => {
        this.logger.log('New version detected');
        this.announce(event.latestVersion?.hash ?? 'sw-version-ready');
      });

    this.unrecoverableSubscription = this.swUpdate.unrecoverable.subscribe((event) => {
      this.logger.error('Service worker in unrecoverable state', { reason: event.reason });
      this.requestRecovery('sw-unrecoverable');
    });
  }

  /**
   * Non-production only: a console handle for exercising the update flow locally,
   * where there is no real deploy to detect and the service worker is opt-in.
   *
   *   __rwUpdate.status()    → which gate the flow is sitting behind
   *   __rwUpdate.simulate()  → force the "new version" state; needs no SW at all
   *
   * Never installed in a production build.
   */
  private installDevHook(): void {
    if (environment.production || typeof window === 'undefined') {
      return;
    }
    const readOptIn = (): string | null => {
      try {
        return localStorage.getItem('rw:sw-local');
      } catch {
        return '<storage unreadable>';
      }
    };
    (window as unknown as Record<string, unknown>)['__rwUpdate'] = {
      status: () => ({
        serviceWorkerEnabled: this.swUpdate.isEnabled,
        hostname: window.location.hostname,
        swLocalOptIn: readOptIn(),
        pollIntervalMs: this.pollIntervalMs,
        lastSeenNgswTimestamp: this.lastSeenNgswTimestamp,
        announcedVersion: this.announcedVersion
      }),
      simulate: () => {
        this.announce(`manual-${Date.now()}`);
        return 'Update announced — the strip should be visible now.';
      }
    };
  }

  private checkForUpdates() {
    this.swUpdate.checkForUpdate().catch(err => {
      this.logger.error('Update check failed', err);
    });
  }

  /**
   * SOFT update detection that works even when server cache headers are wrong:
   * cache-busted fetch of the SW manifest; a changed build timestamp means a
   * new deploy. Emits updateAvailable$ → update-banner (already gated on
   * ProcessingService) shows the non-forcing "Refresh" prompt.
   */
  private async pollNgswManifest(): Promise<void> {
    try {
      const response = await fetch(`ngsw.json?cb=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) { return; }
      const manifest: { timestamp?: unknown } = await response.json();
      const timestamp = manifest?.timestamp;
      if (typeof timestamp !== 'number') { return; }
      if (this.lastSeenNgswTimestamp === undefined) {
        this.lastSeenNgswTimestamp = timestamp;
        return;
      }
      if (timestamp !== this.lastSeenNgswTimestamp) {
        this.lastSeenNgswTimestamp = timestamp;
        this.logger.log('New deploy detected via ngsw.json timestamp');
        this.announce(timestamp);
      }
    } catch {
      // Network hiccup / non-JSON body — ignore; the next interval retries.
    }
  }

  /**
   * How often to check for a new deploy.
   *
   * Derived from the `production` flag every environment file already defines, so
   * this needs no config key of its own: production polls every 2 minutes to keep
   * request volume down, while dev/staging/pre-prod poll every 30s so a deploy is
   * picked up while you are still looking at it.
   */
  private get pollIntervalMs(): number {
    return environment.production ? 2 * 60 * 1000 : 30 * 1000;
  }

  /**
   * Emit updateAvailable$ at most ONCE per detected version. Both detectors (the
   * SW's VERSION_READY and the ngsw.json timestamp poll) fire for the same
   * deploy, so without this guard a single deploy re-arms the banner every poll
   * and would restart an already-running reload countdown.
   */
  private announce(version: number | string): void {
    if (this.announcedVersion === version) {
      return;
    }
    this.announcedVersion = version;
    this.updateAvailableSubject.next(true);
  }

  /**
   * Swap the SW over to the new version WITHOUT replacing the page.
   *
   * BOUNDED ON PURPOSE. `swUpdate.activateUpdate()` messages the service worker
   * and waits for it to confirm, and that promise can never settle — most often
   * when the deploy was spotted by pollNgswManifest() (the ngsw.json timestamp
   * changes the moment a release lands) while the worker has not finished
   * preparing the new version, so there is nothing for it to activate.
   *
   * A hang is not a rejection, so the catch below does not cover it. Both
   * callers replace the document immediately after awaiting this, so an
   * unsettled promise silently strands the user: the button sits on
   * "Refreshing…" and navigation stops reloading. Racing a timer guarantees we
   * always get to the page swap — activation is an optimisation, not a
   * prerequisite, because a full load picks up the new version regardless.
   */
  public async activateUpdate(): Promise<void> {
    // `any` because @types/node and the DOM lib disagree on the timer type here
    // (Timeout vs number); the repo already types timers this way elsewhere.
    let timer: any;
    try {
      await Promise.race([
        this.swUpdate.activateUpdate(),
        new Promise<void>((resolve) => { timer = setTimeout(resolve, ACTIVATION_TIMEOUT_MS); })
      ]);
      this.updateAvailableSubject.next(false);
    } catch (err) {
      this.logger.error('activateUpdate failed — replacing the page anyway', err);
    } finally {
      if (timer !== undefined) { clearTimeout(timer); }
    }
  }

  public async activateUpdateAndReload(): Promise<void> {
    await this.activateUpdate();
    this.reloadWindow();
  }

  /**
   * Apply the update as part of a navigation the user just triggered: load `url`
   * as a real document request instead of an in-app route change. The user asked
   * for a new screen, so they get one — it just happens to be the new build.
   *
   * Uses assign() rather than reload() because the router has not committed the
   * new URL yet; reload() would re-render the screen they are leaving.
   */
  public async activateAndNavigate(url: string): Promise<void> {
    await this.activateUpdate();
    this.navigateWindow(url);
  }

  /**
   * HARD recovery, deferred until the app is idle. Never reloads
   * mid-transaction (Fireblocks signing, in-flight HTTP): waits for
   * ProcessingService to report idle. NOTE: the failing request that triggers
   * recovery still holds the ProcessingInterceptor counter while its error
   * propagates — the debounced idle gate absorbs that, so this MUST NOT be
   * replaced with a synchronous isProcessing check at the call site.
   */
  public requestRecovery(reason: string): void {
    if (this.recoveryPending || this.hasRecoveryBeenAttempted()) {
      return;
    }
    this.recoveryPending = true;
    this.recoverySubscription = this.processingService.isProcessing$
      .pipe(
        debounceTime(50),
        filter((isProcessing) => !isProcessing),
        take(1)
      )
      .subscribe(() => {
        void this.recover(reason);
      });
  }

  /**
   * Hard recovery: unregister service workers, purge CacheStorage, reload —
   * at most ONCE per session. The sessionStorage guard is written
   * SYNCHRONOUSLY before any async work and verified by read-back; if the
   * guard cannot be persisted we refuse to reload (fail toward "stale but
   * stable", never toward a reload loop).
   */
  public async recover(reason: string): Promise<void> {
    if (this.hasRecoveryBeenAttempted()) {
      this.logger.warn('Stale-client recovery already attempted this session — skipping', { reason });
      return;
    }
    if (!this.markRecoveryAttempted()) {
      this.logger.error('Could not persist stale-recovery guard — refusing to reload', { reason });
      return;
    }
    this.logger.warn('Stale client detected — hard recovery starting', { reason });
    await this.clearServiceWorkersAndCaches();
    this.reloadWindow();
  }

  private hasRecoveryBeenAttempted(): boolean {
    try {
      return Number(sessionStorage.getItem(STALE_RECOVERY_STORAGE_KEY) ?? '0') >= 1;
    } catch {
      // Storage unreadable — assume attempted so we can never loop.
      return true;
    }
  }

  private markRecoveryAttempted(): boolean {
    try {
      sessionStorage.setItem(STALE_RECOVERY_STORAGE_KEY, '1');
      return sessionStorage.getItem(STALE_RECOVERY_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Best-effort: each phase isolated so one failure never blocks the reload. */
  private async clearServiceWorkersAndCaches(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }
    } catch (err) {
      this.logger.error('Service worker unregister failed (continuing)', err);
    }
    try {
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch (err) {
      this.logger.error('Cache purge failed (continuing)', err);
    }
  }

  /**
   * The ONLY two places the document is replaced, for the entire update/recovery
   * flow. Do not add others — keeping them here is what makes the behaviour
   * testable (specs spy on these) and auditable.
   */
  private reloadWindow(): void {
    window.location.reload();
  }

  private navigateWindow(url: string): void {
    window.location.assign(url);
  }

  ngOnDestroy(): void {
    this.updateCheckSubscription?.unsubscribe();
    this.versionUpdatesSubscription?.unsubscribe();
    this.unrecoverableSubscription?.unsubscribe();
    this.recoverySubscription?.unsubscribe();
  }
}
